import { useQuery } from '@tanstack/react-query';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '~/components/Spinner';
import { cn } from '~/lib/cn';

import {
  calculateTableTotals,
  getStreamsByDspChart,
  getStreamsByDspTable,
} from '../../../api/streams-by-dsp';
import { retryOn504Options } from '../../../constants/api';
import { useAmplifyUser } from '../../../context/useAmplifyUser';
import { NoAccess } from '../../LabelPerf/components';
import type { SeriesConfig } from '../Components/MultiLineChart';
import { MultiLineChart } from '../Components/MultiLineChart';
import { SummaryTable } from '../Components/SummaryTable';
import type { DspTableRow } from './types';
import { CUSTOMER_NAME_TO_DSP, DSP_COLORS } from './types';

// Chart series configuration builder
const getChartSeries = (t: TFunction): SeriesConfig[] => [
  {
    key: 'spotify',
    color: DSP_COLORS.spotify,
    label: t('reports.dsp.spotify'),
  },
  {
    key: 'youtube',
    color: DSP_COLORS.youtube,
    label: t('reports.dsp.youtube'),
  },
  { key: 'other', color: DSP_COLORS.other, label: t('reports.dsp.other') },
  {
    key: 'appleMusic',
    color: DSP_COLORS.appleMusic,
    label: t('reports.dsp.appleMusic'),
  },
  { key: 'amazon', color: DSP_COLORS.amazon, label: t('reports.dsp.amazon') },
];

// Helper functions - format number with appropriate suffix (T, B, M, K)
const formatNumber = (value: number): string => {
  const absValue = Math.abs(value);

  if (absValue >= 1_000_000_000_000) {
    return (value / 1_000_000_000_000).toFixed(1) + 'T';
  }

  if (absValue >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(1) + 'B';
  }

  if (absValue >= 1_000_000) {
    return (value / 1_000_000).toFixed(1) + 'M';
  }

  if (absValue >= 1_000) {
    return (value / 1_000).toFixed(1) + 'K';
  }

  return value.toFixed(1);
};

const formatChange = (value: number): string => {
  return value.toFixed(1) + '%';
};

// Column cell render functions
const renderDspCell = (info: CellContext<DspTableRow, unknown>) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {info.row.original.dsp}
    </span>
  );
};

const renderThisWeekCell = (info: CellContext<DspTableRow, unknown>) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {formatNumber(info.row.original.thisWeek)}
    </span>
  );
};

const renderFytdCell = (info: CellContext<DspTableRow, unknown>) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {formatNumber(info.row.original.fytd)}
    </span>
  );
};

const renderChangeCell = (info: CellContext<DspTableRow, unknown>) => {
  const value = info.row.original.fytdChange;
  const isTotal = info.row.original.isTotal;

  return (
    <span
      className={cn(
        value >= 0 ? 'text-success' : 'text-error-default',
        isTotal && 'font-bold',
      )}
    >
      {formatChange(value)}
    </span>
  );
};

const renderPercentCell = (info: CellContext<DspTableRow, unknown>) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {info.row.original.fytdPercent}%
    </span>
  );
};

export function GlobalStreamsByDsp() {
  const { t } = useTranslation('label-perf');
  const { user, isLoading: isUserLoading } = useAmplifyUser();

  // Check if user has admin or supervisor role
  const hasPermission = user?.role === 'admin' || user?.role === 'supervisor';

  // Memoize chart series with translations
  const chartSeries = useMemo(() => getChartSeries(t), [t]);

  // Fetch table data
  const {
    data: tableDataRaw,
    isLoading: isTableLoading,
    isError: isTableError,
  } = useQuery({
    queryKey: ['streams-by-dsp-table'],
    queryFn: getStreamsByDspTable,
    ...retryOn504Options,
  });

  // Fetch chart data
  const {
    data: chartData,
    isLoading: isChartLoading,
    isError: isChartError,
  } = useQuery({
    queryKey: ['streams-by-dsp-chart'],
    queryFn: getStreamsByDspChart,
    ...retryOn504Options,
  });

  // Get translated DSP label for "Other"
  const otherLabel = t('common.other');
  const totalLabel = t('common.total');

  // Process table data with totals and percentages
  const { tableData, maxYValue, yTickValues } = useMemo(() => {
    if (!tableDataRaw || tableDataRaw.length === 0) {
      return { tableData: [], maxYValue: 10, yTickValues: [0, 2, 4, 6, 8, 10] };
    }

    const { items, totals } = calculateTableTotals(tableDataRaw);

    // Transform to DspTableRow format
    const rows: DspTableRow[] = items.map((item) => ({
      dsp: CUSTOMER_NAME_TO_DSP[item.customerName] || item.customerName,
      thisWeek: item.thisWeekStreams,
      fytd: item.fytdStreams,
      fytdChange: item.fytdChange,
      fytdPercent: item.fytdPercent,
      isTotal: false,
    }));

    // Sort rows by FYTD descending, but keep "Other" as last row before Total
    rows.sort((a, b) => {
      if (a.dsp === 'Other') {
        return 1;
      }

      if (b.dsp === 'Other') {
        return -1;
      }

      return b.fytd - a.fytd;
    });

    // Translate "Other" label
    const translatedRows = rows.map((row) => ({
      ...row,
      dsp: row.dsp === 'Other' ? otherLabel : row.dsp,
    }));

    // Add totals row
    translatedRows.push({
      dsp: totalLabel,
      thisWeek: totals.thisWeekStreams,
      fytd: totals.fytdStreams,
      fytdChange: totals.fytdChange,
      fytdPercent: totals.fytdPercent,
      isTotal: true,
    });

    // Calculate max Y value for chart based on data
    let maxVal = 0;

    if (chartData) {
      chartData.forEach((point) => {
        const values = [
          point.spotify,
          point.youtube,
          point.appleMusic,
          point.amazon,
          point.other,
        ];

        values.forEach((v) => {
          // Convert to billions for chart display
          const inBillions = v / 1_000_000_000;

          if (inBillions > maxVal) {
            maxVal = inBillions;
          }
        });
      });
    }

    // Round up to nice number
    const roundedMax = Math.ceil(maxVal / 2) * 2;
    const computedMaxY = Math.max(roundedMax, 10);
    const step = computedMaxY / 5;
    const ticks = Array.from({ length: 6 }, (_, i) => i * step);

    return {
      tableData: translatedRows,
      maxYValue: computedMaxY,
      yTickValues: ticks,
    };
  }, [tableDataRaw, chartData, otherLabel, totalLabel]);

  // Transform chart data for display (convert streams to billions)
  const chartDataTransformed = useMemo(() => {
    if (!chartData) {
      return [];
    }

    return chartData.map((point) => ({
      ...point,
      spotify: point.spotify / 1_000_000_000,
      youtube: point.youtube / 1_000_000_000,
      appleMusic: point.appleMusic / 1_000_000_000,
      amazon: point.amazon / 1_000_000_000,
      other: point.other / 1_000_000_000,
    }));
  }, [chartData]);

  // Table columns
  const columns = useMemo<ColumnDef<DspTableRow>[]>(
    () => [
      {
        accessorKey: 'dsp',
        header: t('reports.tables.columns.dsp'),
        cell: renderDspCell,
        size: 170,
      },
      {
        accessorKey: 'thisWeek',
        header: t('reports.tables.columns.thisWeek'),
        cell: renderThisWeekCell,
        size: 100,
      },
      {
        accessorKey: 'fytd',
        header: t('reports.tables.columns.fytd'),
        cell: renderFytdCell,
        size: 100,
      },
      {
        accessorKey: 'fytdChange',
        header: t('reports.tables.columns.fytdChange'),
        cell: renderChangeCell,
        size: 100,
      },
      {
        accessorKey: 'fytdPercent',
        header: t('reports.tables.columns.fytdPercentOfTotal'),
        cell: renderPercentCell,
        size: 130,
      },
    ],
    [t],
  );

  const isLoading = isUserLoading || isTableLoading || isChartLoading;
  const isError = isTableError || isChartError;

  // Loading state - same as catalog dashboards
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  // Permission check - only admin and supervisor roles can access
  if (!hasPermission) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient bg-[length:100%] bg-fixed bg-no-repeat bg-level-01">
        <NoAccess />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient bg-[length:100%] bg-fixed bg-no-repeat bg-level-01">
        <div className="text-center">
          <h2 className="mb-sm text-headline-sm text-default">
            {t('error.failedToLoadData', 'Failed to load data')}
          </h2>
          <p className="text-subtle">
            {t(
              'error.refreshOrContactSupport',
              'Please try refreshing the page or contact support if the issue persists.',
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-w-[800px] grow overflow-auto overscroll-contain bg-gradient bg-[length:100%] bg-fixed bg-no-repeat bg-level-01">
      <div className="p-md sm:p-lg md:p-xl">
        {/* Header */}
        <h1 className="mb-2xl text-headline-sm font-bold text-default">
          {t('reports.pages.globalStreamsByDsp')}
        </h1>

        {/* Chart Section */}
        <div className="mb-2xl">
          <MultiLineChart
            data={chartDataTransformed}
            series={chartSeries}
            yAxisLabel={t('reports.charts.streams')}
            height={380}
            yTickValues={yTickValues}
            yMax={maxYValue}
          />
        </div>

        {/* Table Section */}
        <SummaryTable
          data={tableData}
          columns={columns}
          ariaLabel={t('reports.tables.ariaLabels.globalStreamsByDsp')}
          maxWidth="850px"
        />
      </div>
    </div>
  );
}

GlobalStreamsByDsp.displayName = 'GlobalStreamsByDsp';
